// Numbers in words for the TV board's voice call-out (spec, "TV board": the number is announced as a number,
// "quarante-deux", never "quatre deux"). Deterministic text keeps the announcement the same on every speech engine,
// and leading zeros or receipt numbers never surprise it. 0 to 999 999 covers scan numbers (wrap at 999) and
// receipt numbers (up to 6 digits).
// French: traditional orthography (spaces around "et", "cents" and "quatre-vingts" only when they end the number).
// English: British, with "and" after the hundreds, as the en-GB voice says it.
// Arabic: Modern Standard Arabic in the nominative, the form used to read a number out on its own; units before
// tens joined by "و"; the thousand noun follows the count (ألف, ألفان, آلاف up to ten, ألفًا from eleven).
// The Arabic forms are on the native-speaker review list of R1.3.
import type { Lang } from './ticket';

export const MAX_SPOKEN_NUMBER = 999_999;

/** `n` in words in `lang`. Throws RangeError outside 0 to MAX_SPOKEN_NUMBER; callers fall back to digits. */
export function numberWords(n: number, lang: Lang): string {
  if (!Number.isInteger(n) || n < 0 || n > MAX_SPOKEN_NUMBER) throw new RangeError(`numberWords: ${n} is out of range`);
  switch (lang) {
    case 'fr':
      return fr(n);
    case 'en':
      return en(n);
    case 'ar':
      return ar(n);
  }
}

// ---- French ----

const FR_UNITS = ['zéro', 'un', 'deux', 'trois', 'quatre', 'cinq', 'six', 'sept', 'huit', 'neuf', 'dix', 'onze', 'douze', 'treize', 'quatorze', 'quinze', 'seize', 'dix-sept', 'dix-huit', 'dix-neuf'];
const FR_TENS = ['', '', 'vingt', 'trente', 'quarante', 'cinquante', 'soixante'];

/** 0 to 99. `final` is true when nothing follows in the number, which is when "quatre-vingts" takes its s. */
function frUnder100(n: number, final: boolean): string {
  if (n < 20) return FR_UNITS[n];
  if (n >= 80) {
    const rest = n - 80;
    if (rest === 0) return final ? 'quatre-vingts' : 'quatre-vingt';
    return `quatre-vingt-${FR_UNITS[rest]}`;
  }
  if (n >= 70) {
    const rest = n - 60;
    return rest === 11 ? 'soixante et onze' : `soixante-${FR_UNITS[rest]}`;
  }
  const t = Math.floor(n / 10);
  const u = n % 10;
  if (u === 0) return FR_TENS[t];
  if (u === 1) return `${FR_TENS[t]} et un`;
  return `${FR_TENS[t]}-${FR_UNITS[u]}`;
}

function frUnder1000(n: number, final: boolean): string {
  const h = Math.floor(n / 100);
  const r = n % 100;
  if (h === 0) return frUnder100(r, final);
  const cent = h === 1 ? 'cent' : `${FR_UNITS[h]} cent`;
  if (r === 0) return h > 1 && final ? `${cent}s` : cent;
  return `${cent} ${frUnder100(r, final)}`;
}

function fr(n: number): string {
  if (n < 1000) return frUnder1000(n, true);
  const k = Math.floor(n / 1000);
  const r = n % 1000;
  const mille = k === 1 ? 'mille' : `${frUnder1000(k, false)} mille`;
  return r === 0 ? mille : `${mille} ${frUnder1000(r, true)}`;
}

// ---- English ----

const EN_UNITS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
const EN_TENS = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];

function enUnder100(n: number): string {
  if (n < 20) return EN_UNITS[n];
  const t = Math.floor(n / 10);
  const u = n % 10;
  return u === 0 ? EN_TENS[t] : `${EN_TENS[t]}-${EN_UNITS[u]}`;
}

function enUnder1000(n: number): string {
  const h = Math.floor(n / 100);
  const r = n % 100;
  if (h === 0) return enUnder100(r);
  const hundred = `${EN_UNITS[h]} hundred`;
  return r === 0 ? hundred : `${hundred} and ${enUnder100(r)}`;
}

function en(n: number): string {
  if (n < 1000) return enUnder1000(n);
  const k = Math.floor(n / 1000);
  const r = n % 1000;
  const thousand = `${enUnder1000(k)} thousand`;
  if (r === 0) return thousand;
  return r < 100 ? `${thousand} and ${enUnder100(r)}` : `${thousand} ${enUnder1000(r)}`;
}

// ---- Arabic ----

const AR_UNITS = ['صفر', 'واحد', 'اثنان', 'ثلاثة', 'أربعة', 'خمسة', 'ستة', 'سبعة', 'ثمانية', 'تسعة', 'عشرة', 'أحد عشر', 'اثنا عشر', 'ثلاثة عشر', 'أربعة عشر', 'خمسة عشر', 'ستة عشر', 'سبعة عشر', 'ثمانية عشر', 'تسعة عشر'];
const AR_TENS = ['', '', 'عشرون', 'ثلاثون', 'أربعون', 'خمسون', 'ستون', 'سبعون', 'ثمانون', 'تسعون'];
const AR_HUNDREDS = ['', 'مائة', 'مائتان', 'ثلاثمائة', 'أربعمائة', 'خمسمائة', 'ستمائة', 'سبعمائة', 'ثمانمائة', 'تسعمائة'];
/** The hundreds before "ألف" (construct state): only two hundred changes form. */
const AR_HUNDREDS_OF_THOUSANDS = ['', 'مائة', 'مائتا', 'ثلاثمائة', 'أربعمائة', 'خمسمائة', 'ستمائة', 'سبعمائة', 'ثمانمائة', 'تسعمائة'];

function arUnder100(n: number): string {
  if (n < 20) return AR_UNITS[n];
  const t = Math.floor(n / 10);
  const u = n % 10;
  return u === 0 ? AR_TENS[t] : `${AR_UNITS[u]} و${AR_TENS[t]}`;
}

function arUnder1000(n: number): string {
  const h = Math.floor(n / 100);
  const r = n % 100;
  if (h === 0) return arUnder100(r);
  return r === 0 ? AR_HUNDREDS[h] : `${AR_HUNDREDS[h]} و${arUnder100(r)}`;
}

/** `k` thousands, 1 to 999, with the noun the count calls for. */
function arThousands(k: number): string {
  if (k === 1) return 'ألف';
  if (k === 2) return 'ألفان';
  if (k <= 10) return `${AR_UNITS[k]} آلاف`;
  if (k < 100) return `${arUnder100(k)} ألفًا`;
  const h = Math.floor(k / 100);
  const r = k % 100;
  const head = `${AR_HUNDREDS_OF_THOUSANDS[h]} ألف`;
  return r === 0 ? head : `${head} و${arThousands(r)}`;
}

function ar(n: number): string {
  if (n < 1000) return arUnder1000(n);
  const k = Math.floor(n / 1000);
  const r = n % 1000;
  const thousands = arThousands(k);
  return r === 0 ? thousands : `${thousands} و${arUnder1000(r)}`;
}
